import { useState } from "react";
import { toast } from "sonner";
import { CheckCircle2, Copy, ExternalLink, Download, RotateCcw, SkipForward, Globe, Users, Loader2, AlertTriangle, Undo2, Repeat, XCircle } from "lucide-react";
import { ApprovalBadge, setApproval } from "@/components/Approval";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import api, { copyText, errMsg, fmtDate, imageUrl } from "@/lib/api";

const STATUS = {
  pending: { label: "Menunggu", cls: "bg-amber-500/15 text-amber-300 border-amber-500/30" },
  done: { label: "Selesai", cls: "bg-emerald-500/15 text-emerald-300 border-emerald-500/30" },
  failed: { label: "Gagal", cls: "bg-red-500/15 text-red-300 border-red-500/30" },
  skipped: { label: "Dilangkau", cls: "bg-slate-500/15 text-slate-300 border-slate-500/30" },
};

export function StatusBadge({ status, testId }) {
  const s = STATUS[status];
  return <Badge variant="outline" className={s.cls} data-testid={testId}>{s.label}</Badge>;
}

function GroupActions({ item, due, onChange }) {
  const [busy, setBusy] = useState(false);
  const setStatus = async (status) => {
    setBusy(true);
    try {
      await api.post(`/posts/${item.post_id}/targets/${item.tid}/status`, { status });
      toast.success(status === "done" ? "Ditanda selesai" : status === "skipped" ? "Dilangkau" : "Dikembalikan ke giliran");
      onChange?.();
    } catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); }
  };
  const copyOpen = async () => {
    await copyText(item.text);
    toast.success("Teks disalin. Tampal (paste) dalam group yang dibuka.");
    window.open(item.group_url, "_blank", "noopener,noreferrer");
  };
  const copyOnly = async () => { await copyText(item.text); toast.success("Teks disalin"); };

  if (item.status !== "pending")
    return (
      <Button size="sm" variant="ghost" disabled={busy} onClick={() => setStatus("pending")} data-testid={`queue-undo-btn-${item.tid}`}>
        <Undo2 className="mr-1.5 h-3.5 w-3.5" /> Kembalikan
      </Button>
    );
  return (
    <div className="flex flex-wrap gap-2">
      <Button size="sm" onClick={copyOpen} className="bg-primary hover:bg-primary/90" data-testid={`queue-copy-open-btn-${item.tid}`}>
        <ExternalLink className="mr-1.5 h-3.5 w-3.5" /> Salin & Buka Group
      </Button>
      <Button size="sm" variant="secondary" onClick={copyOnly} data-testid={`queue-copy-text-btn-${item.tid}`}>
        <Copy className="mr-1.5 h-3.5 w-3.5" /> Salin Teks
      </Button>
      {due && (
        <Button size="sm" variant="outline" disabled={busy} onClick={() => setStatus("done")} className="border-emerald-500/40 text-emerald-300 hover:bg-emerald-500/10" data-testid={`queue-mark-posted-btn-${item.tid}`}>
          <CheckCircle2 className="mr-1.5 h-3.5 w-3.5" /> Tanda Selesai
        </Button>
      )}
      <Button size="sm" variant="ghost" disabled={busy} onClick={() => setStatus("skipped")} className="text-muted-foreground" data-testid={`queue-skip-btn-${item.tid}`}>
        <SkipForward className="mr-1.5 h-3.5 w-3.5" /> {due ? "Langkau" : "Batal"}
      </Button>
    </div>
  );
}

function PageActions({ item, onChange }) {
  const [busy, setBusy] = useState(false);
  const retry = async () => {
    setBusy(true);
    try {
      await api.post(`/posts/${item.post_id}/targets/${item.tid}/retry`);
      toast.success("Berjaya dipost ke Facebook Page");
      onChange?.();
    } catch (e) { toast.error(errMsg(e)); onChange?.(); } finally { setBusy(false); }
  };
  if (item.status === "pending")
    return <p className="flex items-center gap-1.5 text-xs text-muted-foreground"><Loader2 className="h-3.5 w-3.5 animate-spin" /> Akan dipost automatik pada masa dijadualkan</p>;
  if (item.status === "failed")
    return (
      <Button size="sm" variant="outline" disabled={busy} onClick={retry} data-testid={`queue-retry-btn-${item.tid}`}>
        {busy ? <Loader2 className="mr-1.5 h-3.5 w-3.5 animate-spin" /> : <RotateCcw className="mr-1.5 h-3.5 w-3.5" />} Cuba Lagi
      </Button>
    );
  return null;
}

function ApprovalActions({ item, onChange }) {
  const [busy, setBusy] = useState(false);
  const act = async (v) => {
    setBusy(true);
    try { await setApproval(item, v); onChange?.(); } catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); }
  };
  return (
    <div className="flex flex-wrap gap-2">
      <Button size="sm" variant="secondary" asChild data-testid={`approval-open-group-btn-${item.tid}`}>
        <a href={item.group_url} target="_blank" rel="noopener noreferrer"><ExternalLink className="mr-1.5 h-3.5 w-3.5" /> Semak di Group</a>
      </Button>
      <Button size="sm" disabled={busy} onClick={() => act("approved")} className="bg-emerald-600 hover:bg-emerald-500" data-testid={`approval-approved-btn-${item.tid}`}>
        <CheckCircle2 className="mr-1.5 h-3.5 w-3.5" /> Diluluskan
      </Button>
      <Button size="sm" variant="outline" disabled={busy} onClick={() => act("rejected")} className="border-red-500/40 text-red-300 hover:bg-red-500/10" data-testid={`approval-rejected-btn-${item.tid}`}>
        <XCircle className="mr-1.5 h-3.5 w-3.5" /> Ditolak
      </Button>
    </div>
  );
}

export default function QueueItem({ item, due, onChange, index = 0 }) {
  const [expanded, setExpanded] = useState(false);
  const isGroup = item.kind === "group";
  return (
    <div className="panel fade-up p-5 transition-colors duration-200 hover:border-primary/40" style={{ animationDelay: `${index * 40}ms` }} data-testid={`queue-item-${item.tid}`}>
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div className="flex min-w-0 items-center gap-3">
          <div className={`flex h-9 w-9 shrink-0 items-center justify-center rounded-lg ${isGroup ? "bg-primary/15 text-primary" : "bg-cyan-500/15 text-cyan-300"}`}>
            {isGroup ? <Users className="h-4 w-4" /> : <Globe className="h-4 w-4" />}
          </div>
          <div className="min-w-0">
            <p className="truncate font-semibold" data-testid={`queue-item-name-${item.tid}`}>{item.group_name}</p>
            <p className="font-mono text-[11px] text-muted-foreground">{fmtDate(item.scheduled_at)}</p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          {item.repeat && item.repeat !== "none" && (
            <span className="flex items-center gap-1 rounded bg-violet-500/15 px-1.5 py-0.5 text-[10px] text-violet-300" data-testid={`queue-item-repeat-${item.tid}`}>
              <Repeat className="h-3 w-3" /> {item.repeat === "daily" ? "Harian" : "Mingguan"}
            </span>
          )}
          {item.source === "autopilot" && (
            <span className="rounded bg-emerald-500/15 px-1.5 py-0.5 text-[10px] text-emerald-300" data-testid={`queue-item-autopilot-${item.tid}`}>AI Autopilot</span>
          )}
          <StatusBadge status={item.status} testId={`queue-item-status-${item.tid}`} />
          <ApprovalBadge approval={item.approval} testId={`queue-item-approval-${item.tid}`} />
        </div>
      </div>
      <p onClick={() => setExpanded(!expanded)} className={`mt-4 cursor-pointer whitespace-pre-wrap text-sm leading-relaxed text-slate-300 ${expanded ? "" : "line-clamp-3"}`} data-testid={`queue-item-text-${item.tid}`}>
        {item.text}
      </p>
      {item.error && <p className="mt-3 flex items-start gap-1.5 text-xs text-red-300"><AlertTriangle className="mt-0.5 h-3.5 w-3.5 shrink-0" />{item.error}</p>}
      {item.image_id && (
        <div className="mt-4 flex items-center gap-3">
          <img src={imageUrl(item.image_id)} alt="Lampiran" className="h-16 w-16 rounded-md border border-border object-cover" />
          {isGroup && (
            <a href={imageUrl(item.image_id)} download className="flex items-center gap-1 text-xs text-primary hover:underline" data-testid={`queue-download-image-${item.tid}`}>
              <Download className="h-3.5 w-3.5" /> Muat turun gambar
            </a>
          )}
          {item.own_image && <span className="rounded bg-violet-500/15 px-1.5 py-0.5 text-[10px] text-violet-300" data-testid={`queue-own-image-${item.tid}`}>Gambar unik AI</span>}
        </div>
      )}
      <div className="mt-4">
        {!isGroup ? <PageActions item={item} onChange={onChange} />
          : item.status === "done" && item.approval === "pending" ? <ApprovalActions item={item} onChange={onChange} />
          : <GroupActions item={item} due={due} onChange={onChange} />}
      </div>
    </div>
  );
}
